import React, { useState, useRef, useEffect } from 'react';
import ShipmentBillUpload from './ShipmentBillUpload';

/**
 * ShipmentModal - Modal component for creating, viewing, and editing shipments
 * Handles: create mode, view mode, edit mode, delete confirmation
 */
const ShipmentModal = ({
  isOpen,
  onClose,
  modalMode,
  selectedShipment,
  // Form state
  invoiceNumber,
  setInvoiceNumber,
  poNumber,
  setPoNumber,
  papsNumber,
  setPapsNumber,
  notes,
  setNotes,
  selectedPallets,
  onRemovePallet,
  palletSearch,
  setPalletSearch,
  showPalletDropdown,
  setShowPalletDropdown,
  filteredPallets,
  onSelectPallet,
  selectedCustomerId,
  customerSearch,
  setCustomerSearch,
  onCustomerSearchChange,
  showCustomerDropdown,
  setShowCustomerDropdown,
  filteredCustomers,
  onSelectCustomer,
  palletRemovalNotice,
  selectedCarrierId,
  setSelectedCarrierId,
  carriers,
  showAddCarrier,
  setShowAddCarrier,
  newCarrierName,
  setNewCarrierName,
  onAddCarrier,
  // View state
  onViewPallet,
  onPalletPhotoUpload,
  onPalletPhotoDelete,
  uploadingPalletId,
  onBillUpload,
  onBillDelete,
  uploadingBill,
  // Actions
  saving,
  totals,
  onConfirmShipment,
  onSaveShipment,
  onCancelEdit,
  onEditShipment,
  onMarkAsShipped,
  onGeneratePackingSlip,
  onGeneratePalletList,
  // Delete
  showDeleteConfirm,
  setShowDeleteConfirm,
  onDeleteShipment,
  isAdmin,
  // Scanner
  onOpenPalletScanner,
  // Helpers
  formatDate,
  // Verification
  onVerifyPallet
}) => {
  // Hooks must run on every render, so this stays above the isOpen early return
  const [showBillHint, setShowBillHint] = useState(false);

  if (!isOpen) return null;

  const billMissing = selectedShipment?.status === 'pending' && !selectedShipment?.bill_path;

  // A natively disabled button swallows hover and click, so the hint would never
  // show. The button is left clickable and marked aria-disabled instead.
  const handleShipClick = () => {
    if (billMissing) {
      setShowBillHint(true);
      setTimeout(() => setShowBillHint(false), 2500);
      return;
    }
    onMarkAsShipped();
  };

  return (
    <div className="modal-overlay">
      <div className="modal shipment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {modalMode === 'create' ? 'Create Shipment' :
             modalMode === 'edit' ? `Edit Shipment ${selectedShipment?.shipment_number}` :
             `Shipment ${selectedShipment?.shipment_number}`}
            {modalMode !== 'create' && selectedShipment?.pallets?.length > 0 && selectedShipment.pallets.every(p => p.verified_by) && (
              <span className="verified-check" title="All pallets verified" style={{marginLeft: '8px'}}>
                <svg viewBox="0 0 24 24" width="18" height="18"><path fill="#fff" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
              </span>
            )}
          </h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body invoice-style">
          {(modalMode === 'create' || modalMode === 'edit') ? (
            <ShipmentForm
              modalMode={modalMode}
              selectedShipment={selectedShipment}
              invoiceNumber={invoiceNumber}
              setInvoiceNumber={setInvoiceNumber}
              poNumber={poNumber}
              setPoNumber={setPoNumber}
              papsNumber={papsNumber}
              setPapsNumber={setPapsNumber}
              notes={notes}
              setNotes={setNotes}
              selectedPallets={selectedPallets}
              onRemovePallet={onRemovePallet}
              palletSearch={palletSearch}
              setPalletSearch={setPalletSearch}
              showPalletDropdown={showPalletDropdown}
              setShowPalletDropdown={setShowPalletDropdown}
              filteredPallets={filteredPallets}
              onSelectPallet={onSelectPallet}
              selectedCustomerId={selectedCustomerId}
              customerSearch={customerSearch}
              setCustomerSearch={setCustomerSearch}
              onCustomerSearchChange={onCustomerSearchChange}
              showCustomerDropdown={showCustomerDropdown}
              setShowCustomerDropdown={setShowCustomerDropdown}
              filteredCustomers={filteredCustomers}
              onSelectCustomer={onSelectCustomer}
              palletRemovalNotice={palletRemovalNotice}
              selectedCarrierId={selectedCarrierId}
              setSelectedCarrierId={setSelectedCarrierId}
              carriers={carriers}
              showAddCarrier={showAddCarrier}
              setShowAddCarrier={setShowAddCarrier}
              newCarrierName={newCarrierName}
              setNewCarrierName={setNewCarrierName}
              onAddCarrier={onAddCarrier}
              onViewPallet={onViewPallet}
              totals={totals}
              onOpenPalletScanner={onOpenPalletScanner}
            />
          ) : (
            <ShipmentViewContent
              selectedShipment={selectedShipment}
              onViewPallet={onViewPallet}
              onPalletPhotoUpload={onPalletPhotoUpload}
              onPalletPhotoDelete={onPalletPhotoDelete}
              uploadingPalletId={uploadingPalletId}
              formatDate={formatDate}
              onVerifyPallet={onVerifyPallet}
              onBillUpload={onBillUpload}
              onBillDelete={onBillDelete}
              uploadingBill={uploadingBill}
            />
          )}
        </div>

        <div className="modal-footer">
          {modalMode === 'view' && (
            <>
              {isAdmin && selectedShipment?.status !== 'shipped' && (
                <button className="shipment-action-btn delete" onClick={() => setShowDeleteConfirm(true)}>
                  Delete
                </button>
              )}
              {selectedShipment?.status === 'pending' && (
                <button
                  className={`shipment-action-btn shipped${billMissing ? ' locked' : ''}`}
                  onClick={handleShipClick}
                  aria-disabled={billMissing}
                >
                  Mark as Shipped
                  {billMissing && (
                    <span className={`ship-lock-tip${showBillHint ? ' show' : ''}`}>
                      Upload the bill to enable this
                    </span>
                  )}
                </button>
              )}
              {isAdmin && selectedShipment?.status !== 'shipped' && (
                <button className="shipment-action-btn edit" onClick={onEditShipment}>
                  Edit
                </button>
              )}
              <button className="shipment-action-btn pallet-list" onClick={onGeneratePalletList}>
                Print Pallet List
              </button>
              <button className="shipment-action-btn packing-slip" onClick={onGeneratePackingSlip}>
                Print Packing Slip
              </button>
            </>
          )}
          {modalMode === 'create' && (
            <>
              <button className="cancel-btn" onClick={onClose}>Cancel</button>
              <button
                className="confirm-btn"
                onClick={onConfirmShipment}
                disabled={saving || selectedPallets.length === 0}
              >
                {saving ? 'Creating...' : 'Confirm Shipment'}
              </button>
            </>
          )}
          {modalMode === 'edit' && (
            <>
              <button className="cancel-btn" onClick={onCancelEdit}>Cancel</button>
              <button
                className="confirm-btn"
                onClick={onSaveShipment}
                disabled={saving || selectedPallets.length === 0}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
        </div>

        {showDeleteConfirm && (
          <div className="delete-confirm-overlay" onClick={(e) => e.stopPropagation()}>
            <div className="delete-confirm-dialog">
              <p>Are you sure you want to delete shipment {selectedShipment?.shipment_number}?</p>
              <p style={{fontSize: '13px', color: '#666'}}>Pallets will be released back to active status.</p>
              <div className="delete-confirm-actions">
                <button className="cancel-btn" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                <button className="delete-btn" onClick={onDeleteShipment}>Delete</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

/**
 * ShipmentForm - Create/Edit form for shipments
 */
const ShipmentForm = ({
  modalMode,
  selectedShipment,
  invoiceNumber,
  setInvoiceNumber,
  poNumber,
  setPoNumber,
  papsNumber,
  setPapsNumber,
  notes,
  setNotes,
  selectedPallets,
  onRemovePallet,
  palletSearch,
  setPalletSearch,
  showPalletDropdown,
  setShowPalletDropdown,
  filteredPallets,
  onSelectPallet,
  selectedCustomerId,
  customerSearch,
  setCustomerSearch,
  onCustomerSearchChange,
  showCustomerDropdown,
  setShowCustomerDropdown,
  filteredCustomers,
  onSelectCustomer,
  palletRemovalNotice,
  selectedCarrierId,
  setSelectedCarrierId,
  carriers,
  showAddCarrier,
  setShowAddCarrier,
  newCarrierName,
  setNewCarrierName,
  onAddCarrier,
  onViewPallet,
  totals,
  onOpenPalletScanner
}) => {
  // The desktop table and the mobile form each render a pallet dropdown; only one is
  // displayed. On short screens it opens below the modal's visible edge, so it is
  // scrolled fully into view whenever it opens or its options first arrive.
  const palletDropdownRefs = useRef([]);
  const hasPalletOptions = filteredPallets.length > 0;
  useEffect(() => {
    if (!showPalletDropdown || !hasPalletOptions) return;
    const shown = palletDropdownRefs.current.find(el => el && el.offsetParent);
    if (shown) shown.scrollIntoView({ block: 'nearest' });
  }, [showPalletDropdown, hasPalletOptions]);

  return (
    <div className="invoice-form">
      {/* Header Row: Customer & Carrier */}
      <div className="invoice-header-row">
        <div className="invoice-customer">
          <label>Customer</label>
          {modalMode === 'edit' ? (
            <div className="invoice-customer-display">{selectedShipment?.customer_name}</div>
          ) : (
            <div className="invoice-search-wrap">
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  if (onCustomerSearchChange) onCustomerSearchChange(e.target.value);
                  setShowCustomerDropdown(true);
                }}
                onFocus={() => setShowCustomerDropdown(true)}
                placeholder="Search customer..."
              />
              {showCustomerDropdown && filteredCustomers.length > 0 && (
                <div className="invoice-dropdown">
                  {filteredCustomers.map(customer => (
                    <div
                      key={customer.id}
                      className="invoice-dropdown-item"
                      onClick={() => onSelectCustomer(customer)}
                    >
                      <strong>{customer.name}</strong>
                      <span>{customer.city}, {customer.state}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {palletRemovalNotice && (
            <div role="status" style={{ marginTop: '6px', fontSize: '12px', color: '#b45309' }}>
              {palletRemovalNotice}
            </div>
          )}
        </div>
        <div className="invoice-customer">
          <label>Carrier</label>
          <div className="carrier-input-row">
            <select
              value={selectedCarrierId}
              onChange={(e) => setSelectedCarrierId(e.target.value)}
              className="invoice-select"
            >
              <option value="">Select carrier...</option>
              {carriers.map(carrier => (
                <option key={carrier.id} value={carrier.id}>{carrier.name}</option>
              ))}
            </select>
            {!showAddCarrier ? (
              <button type="button" className="invoice-add-carrier-btn" onClick={() => setShowAddCarrier(true)}>+</button>
            ) : (
              <>
                <input
                  type="text"
                  value={newCarrierName}
                  onChange={(e) => setNewCarrierName(e.target.value)}
                  placeholder="New carrier"
                  className="invoice-carrier-input"
                  onKeyDown={(e) => e.key === 'Enter' && onAddCarrier()}
                />
                <button type="button" className="invoice-add-btn" onClick={onAddCarrier}>Add</button>
                <button type="button" className="invoice-cancel-btn" onClick={() => { setShowAddCarrier(false); setNewCarrierName(''); }}>×</button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Options Row: Invoice #, PAPS # & PO # */}
      <div className="shipment-options-row">
        <div className="option-group">
          <label>Invoice #</label>
          <input
            type="text"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            placeholder="Defaults to shipment #"
            maxLength={100}
          />
        </div>
        <div className="option-group">
          <label htmlFor="shipment-paps-number">PAPS #</label>
          <input
            id="shipment-paps-number"
            type="text"
            value={papsNumber}
            onChange={(e) => setPapsNumber(e.target.value)}
            placeholder="Optional"
            maxLength={50}
          />
        </div>
        <div className="option-group">
          <label>PO # (Optional)</label>
          <input
            type="text"
            value={poNumber}
            onChange={(e) => setPoNumber(e.target.value)}
            placeholder="Customer PO"
            maxLength={100}
          />
        </div>
      </div>

      {/* Notes */}
      <div className="shipment-notes-row">
        <label>Notes (Optional)</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any special instructions or notes for this shipment..."
          rows={2}
        />
      </div>

      {/* Pallets Table */}
      <div className="invoice-table-wrap">
        <table className="invoice-table">
          <thead>
            <tr>
              <th className="col-pallet">Pallet</th>
              <th className="col-contents">Contents</th>
              <th className="col-weight">Weight</th>
              <th className="col-action"></th>
            </tr>
          </thead>
          <tbody>
            {selectedPallets.map((pallet, index) => (
              <tr key={pallet.id}>
                <td className="col-pallet">
                  <span
                    className="pallet-link"
                    onClick={() => onViewPallet(pallet.id)}
                    title="View pallet details"
                  >
                    {pallet.pallet_number}
                  </span>
                  {pallet.label_text && (
                    <span
                      className="label-badge-inline"
                      style={{ backgroundColor: pallet.label_color || '#757575' }}
                    >
                      {pallet.label_text}
                    </span>
                  )}
                  {pallet.is_full === 1 && (
                    <span className="full-badge-inline">FULL</span>
                  )}
                </td>
                <td className="col-contents">
                  <span className="item-name">{pallet.item_names || 'No items'}</span>
                </td>
                <td className="col-weight">{parseFloat(pallet.total_weight || 0).toFixed(2)} kg</td>
                <td className="col-action">
                  <button className="invoice-remove-btn" onClick={() => onRemovePallet(index)}>×</button>
                </td>
              </tr>
            ))}
            {/* Add Pallet Row */}
            <tr className="add-row">
              <td colSpan="3">
                <div className="invoice-item-search pallet-search-container">
                  <input
                    type="text"
                    value={palletSearch}
                    onChange={(e) => {
                      setPalletSearch(e.target.value);
                      setShowPalletDropdown(true);
                    }}
                    onClick={() => setShowPalletDropdown(!showPalletDropdown)}
                    placeholder={selectedCustomerId ? "Search pallet..." : "Select customer first"}
                    disabled={!selectedCustomerId && modalMode === 'create'}
                  />
                  <button type="button" className="invoice-scan-btn" onClick={onOpenPalletScanner} title="Scan">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                      <path d="M2 4h2v16H2V4zm4 0h1v16H6V4zm3 0h2v16H9V4zm4 0h1v16h-1V4zm3 0h1v16h-1V4zm3 0h2v16h-2V4z"/>
                    </svg>
                  </button>
                  {showPalletDropdown && filteredPallets.length > 0 && (
                    <div className="invoice-dropdown" ref={el => { palletDropdownRefs.current[0] = el; }}>
                      {filteredPallets.map(pallet => (
                        <div
                          key={pallet.id}
                          className="invoice-dropdown-item"
                          onClick={() => onSelectPallet(pallet)}
                        >
                          <div className="dropdown-item-header">
                            <strong>{pallet.pallet_number}</strong>
                            <div className="dropdown-badges">
                              {pallet.is_full === 1 && <span className="badge-full">FULL</span>}
                              {pallet.label_text && (
                                <span className="badge-label" style={{ backgroundColor: pallet.label_color || '#757575' }}>
                                  {pallet.label_text}
                                </span>
                              )}
                            </div>
                          </div>
                          <span>{pallet.item_names || 'No items'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </td>
              <td className="col-action"></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Mobile Form View */}
      <div className="mobile-items-form">
        {selectedPallets.map((pallet, index) => (
          <div key={index} className="mobile-item-card">
            <div className="item-info">
              <span className="item-part">{pallet.pallet_number}</span>
              <span className="item-name">{pallet.item_names || 'No items'}</span>
            </div>
            <div className="item-details">
              <span>{parseFloat(pallet.total_weight || 0).toFixed(1)} kg</span>
            </div>
            <button className="remove-btn" onClick={() => onRemovePallet(index)}>×</button>
          </div>
        ))}

        <div className="mobile-add-form">
          <div className="form-row">
            <label>Add Pallet</label>
            <div className="invoice-item-search">
              <input
                type="text"
                value={palletSearch}
                onChange={(e) => {
                  setPalletSearch(e.target.value);
                  setShowPalletDropdown(true);
                }}
                onClick={() => setShowPalletDropdown(!showPalletDropdown)}
                placeholder={selectedCustomerId ? "Search pallet..." : "Select customer first"}
                disabled={!selectedCustomerId && modalMode === 'create'}
              />
              <button type="button" className="invoice-scan-btn" onClick={onOpenPalletScanner} title="Scan">
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M2 4h2v16H2V4zm4 0h1v16H6V4zm3 0h2v16H9V4zm4 0h1v16h-1V4zm3 0h1v16h-1V4zm3 0h2v16h-2V4z"/>
                </svg>
              </button>
              {showPalletDropdown && filteredPallets.length > 0 && (
                <div className="invoice-dropdown" ref={el => { palletDropdownRefs.current[1] = el; }}>
                  {filteredPallets.map(pallet => (
                    <div
                      key={pallet.id}
                      className="invoice-dropdown-item"
                      onClick={() => onSelectPallet(pallet)}
                    >
                      <div className="dropdown-item-header">
                        <strong>{pallet.pallet_number}</strong>
                        <div className="dropdown-badges">
                          {pallet.is_full === 1 && <span className="badge-full">FULL</span>}
                          {pallet.label_text && (
                            <span className="badge-label" style={{ backgroundColor: pallet.label_color || '#757575' }}>
                              {pallet.label_text}
                            </span>
                          )}
                        </div>
                      </div>
                      <span>{pallet.item_names || 'No items'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Totals */}
      <div className="invoice-totals">
        <div className="invoice-total-row">
          <span>Pallets: {totals.totalPallets}</span>
          <span>Items: {totals.totalItems}</span>
          <span className="total-weight">Total: {totals.totalWeight.toFixed(2)} kg</span>
        </div>
      </div>
    </div>
  );
};

/**
 * ShipmentViewContent - View-only display of shipment details
 */
const ShipmentViewContent = ({
  selectedShipment,
  onViewPallet,
  onPalletPhotoUpload,
  onPalletPhotoDelete,
  uploadingPalletId,
  formatDate,
  onVerifyPallet,
  onBillUpload,
  onBillDelete,
  uploadingBill
}) => {
  const [verifyingPalletId, setVerifyingPalletId] = useState(null);
  const [verifyName, setVerifyName] = useState('');
  const [previewImage, setPreviewImage] = useState(null);

  const getImageUrl = (imagePath) =>
    `${process.env.REACT_APP_API_URL?.replace('/api', '') || ''}/uploads/${imagePath}`;

  const handleVerifyClick = (palletId) => {
    setVerifyingPalletId(palletId);
    setVerifyName('');
  };

  const handleVerifyConfirm = async () => {
    if (!verifyName.trim()) return;
    await onVerifyPallet(verifyingPalletId, verifyName.trim());
    setVerifyingPalletId(null);
    setVerifyName('');
  };
  return (
    <div className="shipment-view-content">
      {previewImage && (
        <div className="photo-preview-overlay" onClick={() => setPreviewImage(null)}>
          <button className="photo-preview-close" onClick={() => setPreviewImage(null)}>✕</button>
          <img src={previewImage} alt="Pallet photo" className="photo-preview-img" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
      {/* Packing slip style header with cells */}
      <div className="view-header-grid">
        {/* Row 1: Customer and Status */}
        <div className="view-cell view-cell-customer">
          <div className="view-cell-label">Customer</div>
          <div className="view-cell-value">{selectedShipment?.customer_name}</div>
        </div>
        <div className="view-cell view-cell-status">
          <div className="view-cell-label">Status</div>
          <div className={`view-cell-badge status-${selectedShipment?.status || 'pending'}`}>
            {selectedShipment?.status === 'shipped' ? 'SHIPPED' : 'PENDING'}
          </div>
        </div>

        {/* Row 2: Invoice #, PO #, PAPS #, Date */}
        <div className="view-cell">
          <div className="view-cell-label">Invoice #</div>
          <div className="view-cell-value">{selectedShipment?.invoice_number || '-'}</div>
        </div>
        <div className="view-cell">
          <div className="view-cell-label">PO #</div>
          <div className="view-cell-value">{selectedShipment?.po_number || '-'}</div>
        </div>
        <div className="view-cell">
          <div className="view-cell-label">PAPS #</div>
          <div className="view-cell-value">{selectedShipment?.paps_number || '-'}</div>
        </div>
        <div className="view-cell">
          <div className="view-cell-label">Date</div>
          <div className="view-cell-value">{formatDate(selectedShipment?.created_at)}</div>
        </div>

        {/* Row 3: Carrier, Pallets, Items, Weight */}
        <div className="view-cell">
          <div className="view-cell-label">Carrier</div>
          <div className="view-cell-value">{selectedShipment?.carrier_name || '-'}</div>
        </div>
        <div className="view-cell">
          <div className="view-cell-label">Pallets</div>
          <div className="view-cell-value">{selectedShipment?.pallets?.length || 0}</div>
        </div>
        <div className="view-cell">
          <div className="view-cell-label">Total Items</div>
          <div className="view-cell-value">{selectedShipment?.items?.reduce((sum, item) => sum + parseInt(item.total_quantity || 0), 0) || 0} cases</div>
        </div>
        <div className="view-cell">
          <div className="view-cell-label">Total Weight</div>
          <div className="view-cell-value">{parseFloat(selectedShipment?.total_weight || 0).toFixed(2)} kg</div>
        </div>

        {/* Notes Row */}
        {selectedShipment?.notes && (
          <div className="view-cell view-cell-notes">
            <div className="view-cell-label">Notes</div>
            <div className="view-cell-value">{selectedShipment.notes}</div>
          </div>
        )}
      </div>

      <ShipmentBillUpload
        shipment={selectedShipment}
        onBillUpload={onBillUpload}
        onBillDelete={onBillDelete}
        uploadingBill={uploadingBill}
        formatDate={formatDate}
      />

      <div className="pallets-section">
        <h3>Pallets ({selectedShipment?.pallets?.length || 0})</h3>
        <table className="view-pallets-table">
          <thead>
            <tr>
              <th>Pallet #</th>
              <th>Lot #</th>
              <th>Items</th>
              <th>Weight</th>
              <th>Verified By</th>
              <th>Photo</th>
            </tr>
          </thead>
          <tbody>
            {selectedShipment?.pallets?.map((pallet) => (
              <tr key={pallet.id}>
                <td className="pallet-number">
                  <span
                    className="pallet-link"
                    onClick={() => onViewPallet(pallet.id)}
                    title="View pallet details"
                  >
                    👁 {pallet.pallet_number}
                  </span>
                  {pallet.label_text && (
                    <span className={`pallet-label-badge small ${pallet.label_color || 'gray'}`}>
                      {pallet.label_text}
                    </span>
                  )}
                  {pallet.is_full === 1 && (
                    <span className="pallet-full-badge small">FULL</span>
                  )}
                </td>
                <td>{pallet.lot_number}</td>
                <td>{pallet.item_count} items</td>
                <td>{parseFloat(pallet.total_weight || 0).toFixed(2)} kg</td>
                <td className="col-verify">
                  {pallet.verified_by ? (
                    <label className="verify-done" title={pallet.verified_at ? `Verified at ${new Date(pallet.verified_at).toLocaleString()}` : ''}>
                      <input type="checkbox" checked disabled style={{accentColor: '#2e7d32', width: '16px', height: '16px'}} />
                      <span className="verified-badge">{pallet.verified_by}</span>
                    </label>
                  ) : verifyingPalletId === pallet.id ? (
                    <div className="verify-inline">
                      <input
                        type="text"
                        value={verifyName}
                        onChange={(e) => setVerifyName(e.target.value)}
                        placeholder="Your name"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleVerifyConfirm();
                          if (e.key === 'Escape') setVerifyingPalletId(null);
                        }}
                        style={{padding: '4px 8px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', width: '120px'}}
                      />
                      <button
                        onClick={handleVerifyConfirm}
                        disabled={!verifyName.trim()}
                        style={{padding: '4px 10px', fontSize: '11px', fontWeight: 600, border: 'none', borderRadius: '4px', cursor: verifyName.trim() ? 'pointer' : 'not-allowed', backgroundColor: verifyName.trim() ? '#2e7d32' : '#ccc', color: '#fff'}}
                      >OK</button>
                      <button
                        onClick={() => setVerifyingPalletId(null)}
                        style={{padding: '4px 8px', fontSize: '11px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', backgroundColor: '#fff'}}
                      >X</button>
                    </div>
                  ) : (
                    <label className="verify-checkbox" onClick={() => handleVerifyClick(pallet.id)} style={{cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'}}>
                      <input type="checkbox" checked={false} readOnly style={{width: '16px', height: '16px', cursor: 'pointer'}} />
                      <span style={{color: '#999', fontSize: '12px'}}>Verify</span>
                    </label>
                  )}
                </td>
                <td className="pallet-photo-cell">
                  {pallet.image_path ? (
                    <div className="photo-wrapper">
                      <img
                        src={getImageUrl(pallet.image_path)}
                        alt="Pallet"
                        className="pallet-thumbnail"
                        onClick={() => setPreviewImage(getImageUrl(pallet.image_path))}
                      />
                      <button
                        className="photo-delete-btn"
                        title="Delete photo"
                        onClick={(e) => { e.stopPropagation(); onPalletPhotoDelete(pallet.id); }}
                      >
                        &times;
                      </button>
                    </div>
                  ) : (
                    uploadingPalletId === pallet.id ? (
                      <span className="uploading-spinner">⏳</span>
                    ) : (
                      <div className="photo-btn-group">
                        <label className="camera-btn" title="Take photo">
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={(e) => onPalletPhotoUpload(pallet.id, e)}
                            style={{ display: 'none' }}
                          />
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 15.2c1.77 0 3.2-1.43 3.2-3.2S13.77 8.8 12 8.8 8.8 10.23 8.8 12s1.43 3.2 3.2 3.2zm0-5.2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2z"/>
                            <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/>
                          </svg>
                        </label>
                        <label className="gallery-btn" title="Choose from gallery">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => onPalletPhotoUpload(pallet.id, e)}
                            style={{ display: 'none' }}
                          />
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                          </svg>
                        </label>
                      </div>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default ShipmentModal;
